import * as Sentry from '@sentry/nextjs';
import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  // Changing any value in this array re-mounts the boundary and clears the
  // error state (e.g. pass the current route/query so a retry can recover).
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains render/runtime errors from a subtree so one throwing component
 * (e.g. Google Maps' InvalidValueError on a bad coordinate) degrades to a
 * fallback instead of taking down the whole page with "Application error".
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys } = this.props;
    if (
      this.state.hasError &&
      resetKeys &&
      prevProps.resetKeys &&
      resetKeys.some((key, i) => key !== prevProps.resetKeys?.[i])
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
